// bgmPlayer.js
const DEFAULT_BGM_URL =
	"https://test.goodglows.com/media/story/glowloop_spacehardcore_1.mp3";
const QUEST_BGM_URL =
	"https://test.goodglows.com/media/story/quest_BGM_longer.mp3";

let audioCtx = null;
let bgmBuffer = null;
let bgmSource = null;
let bgmGain = null;
let isInitialized = false;
let isPlaying = false;
let currentVolume = 1.0;
let isMuted = false;
let currentBgmUrl = DEFAULT_BGM_URL;
let bgmStartCtxTime = 0;
let bgmStartOffset = 0;
let lastKnownPosition = 0;

function getBgmDuration() {
	return bgmBuffer?.duration || 0;
}

function normalizePosition(position = 0) {
	const duration = getBgmDuration();
	if (!duration || !isFinite(duration)) {
		return Math.max(0, position || 0);
	}

	const normalized = position % duration;
	return normalized < 0 ? normalized + duration : normalized;
}

function updateLastKnownPosition() {
	if (!bgmBuffer) {
		lastKnownPosition = 0;
		return lastKnownPosition;
	}

	if (isPlaying && audioCtx) {
		const elapsed = Math.max(0, audioCtx.currentTime - bgmStartCtxTime);
		const position = bgmStartOffset + elapsed;
		const duration = getBgmDuration();
		lastKnownPosition = duration ? position % duration : position;
	}

	return lastKnownPosition;
}

export function getAudioContext() {
	if (!audioCtx) {
		console.log("🎹 Creating NEW AudioContext (first time)");
		audioCtx = new (window.AudioContext || window.webkitAudioContext)();
	} else {
		console.log("🎹 Reusing existing AudioContext - state:", audioCtx.state);
	}
	return audioCtx;
}

export function getBgmAudio() {
	if (!isInitialized) {
		return null;
	}

	return {
		get volume() {
			return currentVolume;
		},
		set volume(value) {
			setBgmVolume(value);
		},
	};
}

function applyGainValue() {
	if (!bgmGain) {return;}
	const ctx = getAudioContext();
	const target = isMuted ? 0 : currentVolume;
	bgmGain.gain.setValueAtTime(target, ctx.currentTime);
}

export async function initBgm(url, volume = 1.0, muted = false) {
	const ctx = getAudioContext();

	if (!bgmGain) {
		bgmGain = ctx.createGain();
		bgmGain.connect(ctx.destination);
	}

	currentVolume = volume;
	isMuted = muted;
	applyGainValue();

	// Load & decode audio buffer
	const response = await fetch(url);
	const arrayBuffer = await response.arrayBuffer();
	bgmBuffer = await ctx.decodeAudioData(arrayBuffer);

	isInitialized = true;
	currentBgmUrl = url;
}

export async function playBgm(startPosition = null) {
	const ctx = getAudioContext();
	if (!isInitialized || !bgmBuffer) {
		console.warn("⚠️ playBgm called but not initialized or no buffer");
		return;
	}

	console.log("🎵 Playing BGM - AudioContext state:", ctx.state);

	const duration = getBgmDuration();
	const offset =
		startPosition !== null
			? normalizePosition(startPosition)
			: normalizePosition(lastKnownPosition || bgmStartOffset || 0);

	// MUST create a new source each play (Web Audio rule)
	bgmSource = ctx.createBufferSource();
	bgmSource.buffer = bgmBuffer;
	bgmSource.loop = true;
	bgmSource.connect(bgmGain);

	bgmSource.start(0, duration ? offset % duration : offset);
	bgmStartCtxTime = ctx.currentTime;
	bgmStartOffset = offset;
	lastKnownPosition = offset;
	isPlaying = true;
	console.log("✅ BGM BufferSource started, isPlaying:", isPlaying);
}

export function pauseBgm() {
	if (bgmSource && isPlaying) {
		updateLastKnownPosition();
		bgmStartOffset = lastKnownPosition;
		try {
			bgmSource.stop(0);
		} catch {}
		bgmSource.disconnect();
		bgmSource = null;
		isPlaying = false;
	}
}

export async function resumeBgm() {
	if (isPlaying) {
		console.log("🔁 resumeBgm called but already playing - skipping");
		return;
	}

	console.log("🔁 Resuming BGM - bgmBuffer exists:", !!bgmBuffer);

	// Just re-play (because BufferSource cannot "resume")
	if (bgmBuffer) {
		await playBgm(lastKnownPosition);
	} else {
		console.warn("⚠️ resumeBgm called but no bgmBuffer available");
	}
}

export async function resumeAudioContext() {
	const ctx = getAudioContext();
	if (ctx.state === "suspended") {
		try {
			await ctx.resume();
			return true;
		} catch {
			return false;
		}
	}
	return true;
}

export function isBgmPlaying() {
	return isPlaying;
}

export function getBgmPosition() {
	return updateLastKnownPosition();
}

export function setBgmPosition(position = 0) {
	const normalized = normalizePosition(position);
	lastKnownPosition = normalized;
	bgmStartOffset = normalized;
	return normalized;
}

export function setBgmVolume(value) {
	currentVolume = value;
	applyGainValue();
}

export function fadeBgmTo(value, duration = 0.5) {
	if (!bgmGain) {return;}
	const ctx = getAudioContext();
	bgmGain.gain.cancelScheduledValues(ctx.currentTime);
	currentVolume = value;
	const target = isMuted ? 0 : value;
	bgmGain.gain.linearRampToValueAtTime(target, ctx.currentTime + duration);
}

export function setBgmMuted(muted) {
	isMuted = muted;
	applyGainValue();
}

export function toggleBgmMuted() {
	isMuted = !isMuted;
	applyGainValue();
	return isMuted;
}

export function isBgmMuted() {
	return isMuted;
}

export async function ensureBgmStarted({
	url = DEFAULT_BGM_URL,
	volume = 0.1,
	muted = false,
	startPosition = null,
} = {}) {
	const needsNewBuffer = !isInitialized || currentBgmUrl !== url;
	if (startPosition !== null) {
		setBgmPosition(startPosition);
	}

	if (needsNewBuffer) {
		if (isInitialized && currentBgmUrl !== url) {
			pauseBgm();
		}
		await initBgm(url, volume, muted);
		if (!muted) {
			await playBgm(startPosition ?? lastKnownPosition);
		}
		return;
	}

	const bgm = getBgmAudio();

	if (!bgm) {
		await initBgm(url, volume, muted);
		if (!muted) {
			await playBgm(startPosition ?? lastKnownPosition);
		}
		return;
	}

	setBgmVolume(volume);
	setBgmMuted(muted);

	if (!isBgmPlaying() && !muted) {
		await resumeBgm();
	}
}

export { DEFAULT_BGM_URL, QUEST_BGM_URL };

export function getCurrentBgmUrl() {
	return currentBgmUrl;
}

export function getBgmDebugInfo() {
	const ctx = audioCtx;
	return {
		url: currentBgmUrl,
		initialized: isInitialized,
		isPlaying,
		isMuted,
		volume: currentVolume,
		audioContextState: ctx?.state || "unknown",
	};
}

// Cleanup function for sign-out or component unmount
export function cleanupBgm() {
	console.log("🧹 Cleaning up BGM player");

	// Stop playback
	if (bgmSource && isPlaying) {
		try {
			bgmSource.stop(0);
			bgmSource.disconnect();
		} catch (e) {
			console.warn("Error stopping BGM source:", e);
		}
		bgmSource = null;
	}

	// Disconnect gain node
	if (bgmGain) {
		try {
			bgmGain.disconnect();
		} catch (e) {
			console.warn("Error disconnecting gain:", e);
		}
		bgmGain = null;
	}

	// Close audio context
	if (audioCtx) {
		try {
			audioCtx.close();
		} catch (e) {
			console.warn("Error closing audio context:", e);
		}
		audioCtx = null;
	}

	// Reset all state
	bgmBuffer = null;
	isInitialized = false;
	isPlaying = false;
	currentVolume = 1.0;
	isMuted = false;
	currentBgmUrl = DEFAULT_BGM_URL;
	bgmStartCtxTime = 0;
	bgmStartOffset = 0;
	lastKnownPosition = 0;

	// Clear localStorage BGM preferences
	try {
		localStorage.removeItem("bgmShouldPlay");
		localStorage.removeItem("bgmUrl");
		localStorage.removeItem("bgmVolume");
		localStorage.removeItem("bgmMuted");
	} catch (e) {
		console.warn("Error clearing BGM localStorage:", e);
	}

	console.log("✅ BGM cleanup complete");
}
